import { Star } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { MarqueeOnHover } from '@/components/ui/marquee-on-hover';
import { TokenIcon } from '@/features/tokenPairs';
import type { SpeedMarket } from '@/features/tokenPairs';
import { cn } from '@/lib/utils';

import {
    useMarketSparkline,
    useOneShotVisible,
} from '../hooks/useMarketSparkline';
import { useTradingStore } from '../store';

export function formatMarkPrice(markPx: string | null): string | null {
    if (markPx === null) return null;
    const value = Number(markPx);
    if (!Number.isFinite(value)) return null;
    // "$" because every market quotes in USDC (dollar-pegged). Pin the digit
    // count (min = max) so ticking prices keep a constant width instead of
    // jumping as trailing zeros appear and disappear.
    if (value >= 1000) {
        return `$${value.toLocaleString('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        })}`;
    }
    return `$${value.toLocaleString('en-US', {
        minimumSignificantDigits: 5,
        maximumSignificantDigits: 5,
    })}`;
}

export function formatFunding(funding: string | null): string | null {
    if (funding === null) return null;
    const value = Number(funding);
    if (!Number.isFinite(value)) return null;
    const pct = value * 100;
    return `${pct > 0 ? '+' : ''}${pct.toFixed(4)}%`;
}

export interface DayChange {
    text: string;
    dir: 1 | 0 | -1;
}

/** 24h move vs Hyperliquid's prevDayPx — the "is it up or down today" datum. */
export function formatDayChange(
    markPx: string | null,
    prevDayPx: string | null,
): DayChange | null {
    if (markPx === null || prevDayPx === null) return null;
    const mark = Number(markPx);
    const prev = Number(prevDayPx);
    if (!Number.isFinite(mark) || !Number.isFinite(prev) || prev <= 0) {
        return null;
    }
    const pct = ((mark - prev) / prev) * 100;
    const dir: DayChange['dir'] = pct > 0.005 ? 1 : pct < -0.005 ? -1 : 0;
    const digits = Math.abs(pct) >= 10 ? 1 : 2;
    return { text: `${pct > 0 ? '+' : ''}${pct.toFixed(digits)}%`, dir };
}

/** 24h notional volume, abbreviated: "$1.26B", "$510.8M", "$78.2K". */
export function formatDayVolume(dayNtlVlm: string | null): string | null {
    if (dayNtlVlm === null) return null;
    const value = Number(dayNtlVlm);
    if (!Number.isFinite(value) || value <= 0) return null;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
}

/**
 * Open interest as a USD notional: "$2.82B", "$55.9M", "$210K".
 *
 * `openInterest` arrives in COIN units (BTC's is ~35,470), which is unreadable
 * next to a price and incomparable between markets. Multiplied by the mark it
 * becomes the standard "how crowded is this trade" figure, and it spans four
 * orders of magnitude across our board — $2.82B on BTC, under $1M on 156 of
 * the 353 markets — so it is abbreviated rather than laid out in full.
 */
export function formatOpenInterest(
    openInterest: string | null,
    markPx: string | null,
): string | null {
    if (openInterest === null || markPx === null) return null;
    const size = Number(openInterest);
    const mark = Number(markPx);
    if (!Number.isFinite(size) || !Number.isFinite(mark)) return null;
    if (size <= 0 || mark <= 0) return null;
    const usd = size * mark;
    if (usd >= 1e9) return `$${(usd / 1e9).toFixed(2)}B`;
    if (usd >= 1e6) return `$${(usd / 1e6).toFixed(1)}M`;
    if (usd >= 1e3) return `$${(usd / 1e3).toFixed(1)}K`;
    return `$${usd.toFixed(0)}`;
}

/**
 * How far the mark has drifted from the oracle, in basis points.
 *
 * Both prices are already in every markets response and neither was shown. The
 * gap matters to a leveraged trader for a concrete reason: Hyperliquid
 * liquidates against the MARK, while funding is driven by the ORACLE. At 40x a
 * 1% gap is wider than the whole maintenance margin.
 *
 * Measured across the live board, the distribution is tight where it counts —
 * median 6bps, p90 43bps — and the enormous outliers are all dead markets:
 * STRAX at 3,705%, FRIEND at 889%, every one of them with $0 open interest.
 * So this doubles as a "nothing trades here, the mark is stale" detector,
 * which is exactly what a trader about to take leverage needs to know.
 */
export function markOracleSpreadBps(
    markPx: string | null,
    oraclePx: string | null,
): number | null {
    if (markPx === null || oraclePx === null) return null;
    const mark = Number(markPx);
    const oracle = Number(oraclePx);
    if (!Number.isFinite(mark) || !Number.isFinite(oracle)) return null;
    if (mark <= 0 || oracle <= 0) return null;
    return ((mark - oracle) / oracle) * 10_000;
}

/**
 * Above this, the gap is worth interrupting someone about. Set at 1% because
 * p95 of the live board is 90bps: below it the number is ordinary market
 * noise, and a permanent 6bps readout on BTC would be clutter that trains
 * people to ignore the row.
 */
export const DISLOCATION_BPS = 100;

/**
 * The gap as a percentage, for the warning chip: "1.2%", "37%".
 *
 * Deliberately loses precision as it grows. A market 3,705% away from its
 * oracle (STRAX, live, right now) does not need two decimal places — it needs
 * the reader to understand the quote is meaningless.
 */
export function formatDislocation(
    markPx: string | null,
    oraclePx: string | null,
): string | null {
    const bps = markOracleSpreadBps(markPx, oraclePx);
    if (bps === null) return null;
    const pct = Math.abs(bps) / 100;
    if (pct >= 100) return '>100%';
    if (pct >= 10) return `${pct.toFixed(0)}%`;
    return `${pct.toFixed(1)}%`;
}

export function isDislocated(
    markPx: string | null,
    oraclePx: string | null,
): boolean {
    const bps = markOracleSpreadBps(markPx, oraclePx);
    return bps !== null && Math.abs(bps) >= DISLOCATION_BPS;
}

const SPARK_W = 48;
const SPARK_H = 14;

/** Tiny 24h close-price polyline, colored by the day's direction. */
const MarketSparkline: React.FC<{ closes: number[]; dir: DayChange['dir'] }> =
    React.memo(({ closes, dir }) => {
        if (closes.length < 2) return null;
        const min = Math.min(...closes);
        const max = Math.max(...closes);
        const span = max - min || 1;
        const step = SPARK_W / (closes.length - 1);
        const points = closes
            .map((close, i) => {
                const x = (i * step).toFixed(1);
                const y = (
                    SPARK_H -
                    1.5 -
                    ((close - min) / span) * (SPARK_H - 3)
                ).toFixed(1);
                return `${x},${y}`;
            })
            .join(' ');
        const stroke =
            dir > 0 ? '#C8F224' : dir < 0 ? '#FF5C6E' : 'rgba(131,136,116,0.9)';
        return (
            <svg
                width={SPARK_W}
                height={SPARK_H}
                viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
                className="shrink-0"
                aria-hidden="true"
            >
                <polyline
                    points={points}
                    fill="none"
                    stroke={stroke}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    });

MarketSparkline.displayName = 'MarketSparkline';

/**
 * Live price for the SELECTED market's row, streamed from the price
 * WebSocket via the store. Isolated in its own component so the ~15Hz
 * updates re-render one number, not the whole (memoized) market list.
 * Other rows show the markets endpoint's markPx snapshot. Flashes
 * lime/red when the DISPLAYED price changes (not every raw tick).
 */
const LiveSelectedPrice: React.FC<{
    fallback: string | null;
    markPx: string | null;
}> = ({ fallback, markPx }) => {
    const currentValue = useTradingStore((s) => s.currentValue);

    // Right after a market switch, the store still carries the PREVIOUS
    // market's price until the new feed ticks. The ≤30s markPx snapshot is
    // the ground truth for magnitude: a "live" value more than 10% away
    // can't plausibly be this market — show the snapshot until it agrees.
    const snapshot = markPx === null ? null : Number(markPx);
    const plausible =
        currentValue > 0 &&
        (snapshot === null ||
            !Number.isFinite(snapshot) ||
            Math.abs(currentValue - snapshot) / snapshot <= 0.1);

    const live = plausible ? formatMarkPrice(String(currentValue)) : null;
    const price = live ?? fallback;

    const prevPriceRef = useRef<string | null>(null);
    const [flash, setFlash] = useState<'up' | 'down' | null>(null);

    useEffect(() => {
        const prev = prevPriceRef.current;
        prevPriceRef.current = price;
        if (!price || !prev || price === prev) return;
        const toNumber = (p: string) => Number(p.replace(/[$,]/g, ''));
        const direction = toNumber(price) > toNumber(prev) ? 'up' : 'down';
        setFlash(direction);
        const timer = setTimeout(() => setFlash(null), 320);
        return () => clearTimeout(timer);
    }, [price]);

    if (!price) return null;
    return (
        <span
            className={cn(
                'font-mono text-xs font-semibold tabular-nums text-foreground',
                flash === 'up' && 'price-flash-up',
                flash === 'down' && 'price-flash-down',
            )}
        >
            {price}
        </span>
    );
};

/**
 * Short chip label for a market the backend reports as not tradeable
 * (HIP-3 builder markets: rollback-disabled or stale feed).
 * Rows stay visible and selectable so an existing position can still be
 * watched/closed; only opening is blocked server-side.
 */
export function unavailableLabel(market: SpeedMarket): string | null {
    if (market.tradeable !== false) return null;
    switch (market.unavailableReason) {
        case 'hip3Disabled':
            return 'disabled';
        case 'staleMarketData':
            return 'stale data';
        case 'isolatedMarginUnsupported':
            return null;
        default:
            return 'not tradeable';
    }
}

interface MarketRowProps {
    market: SpeedMarket;
    isSelected: boolean;
    /** this market already has an open position — marked, never disabled. */
    held?: boolean;
    onSelect: () => void;
    favorite?: boolean;
    onToggleFavorite?: () => void;
}

/**
 * One market as a scannable card: framed icon tile tinted by the day's
 * direction, bright mono price, bold lime/red 24h change, lazy 24h
 * sparkline, and a pin star. Funding and volume stay in the tooltip so the
 * card answers the one scan question: up or down today?
 *
 * Open interest and the oracle warning are the exceptions, on the card
 * itself: this component is also the phone's market picker (via
 * MobileMarketSheet), where a `title` tooltip does not exist at all, and both
 * answer a question the price cannot — is there anything here to trade?
 */
export const MarketRow: React.FC<MarketRowProps> = React.memo(
    ({
        market,
        isSelected,
        held = false,
        onSelect,
        favorite,
        onToggleFavorite,
    }) => {
        const price = formatMarkPrice(market.markPx);
        const change = formatDayChange(market.markPx, market.prevDayPx ?? null);
        const funding = formatFunding(market.funding);
        const volume = formatDayVolume(market.dayNtlVlm ?? null);
        const dir = change?.dir ?? 0;

        // Sparkline data is fetched once the row scrolls near the viewport.
        const { ref, visible } = useOneShotVisible<HTMLButtonElement>();
        const { data: closes } = useMarketSparkline(market.coin, visible);

        const unavailable = unavailableLabel(market);
        const isolatedOnly = market.onlyIsolated === true;
        // Both were already on `market` and shown nowhere. Open interest joins
        // the tooltip beside volume and funding; the mark/oracle gap earns a
        // chip instead, because it is the one that should stop a selection.
        const openInterest = formatOpenInterest(
            market.openInterest,
            market.markPx,
        );
        const dislocation = isDislocated(market.markPx, market.oraclePx)
            ? formatDislocation(market.markPx, market.oraclePx)
            : null;

        const tooltip = [
            volume && `24h vol ${volume}`,
            openInterest && `Open interest ${openInterest}`,
            funding && `Funding ${funding}`,
            `${market.maxLeverage}× max leverage`,
            unavailable && `Not tradeable right now (${unavailable})`,
            dislocation &&
                `Mark is ${dislocation} from the oracle — usually a dead market`,
            isolatedOnly && 'Isolated margin only',
        ]
            .filter(Boolean)
            .join(' · ');

        return (
            // The select action is a stretched button UNDER the content (which
            // is pointer-events-none) so the pin can be a real sibling button —
            // interactive controls must not nest (WCAG 4.1.2).
            <div
                className={cn(
                    'group relative mx-2 mb-1 flex w-[calc(100%-1rem)] items-center gap-2.5 rounded-xl border p-2 text-left transition-all',
                    'shadow-[inset_0_1px_0_rgba(236,238,228,0.04)]',
                    isSelected
                        ? 'border-primary/35 bg-secondary/70'
                        : 'border-border/60 bg-secondary/30 hover:-translate-y-px hover:border-border hover:bg-secondary/60',
                    // A held market is highlighted, not dimmed: it is somewhere
                    // you want to go back to, not somewhere you are blocked from.
                    held && !isSelected && 'border-primary/25',
                )}
            >
                <button
                    ref={ref}
                    type="button"
                    onClick={() => !isSelected && onSelect()}
                    title={held ? `${tooltip} · Position open` : tooltip}
                    aria-label={
                        held
                            ? `Select ${market.coin} market (position open)`
                            : `Select ${market.coin} market`
                    }
                    aria-current={isSelected || undefined}
                    className="absolute inset-0 cursor-pointer rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed"
                />

                {isSelected && (
                    <span className="pointer-events-none absolute inset-y-2 left-0 w-0.75 rounded-full bg-primary shadow-[0_0_8px_rgba(200,242,36,0.5)]" />
                )}

                {/* Icon tile — framed block, tinted by the day's direction */}
                <span
                    className={cn(
                        'pointer-events-none flex size-9 shrink-0 items-center justify-center rounded-lg border',
                        dir > 0 && 'border-primary/25 bg-primary/[0.07]',
                        dir < 0 &&
                            'border-destructive/25 bg-destructive/[0.07]',
                        dir === 0 && 'border-border bg-secondary/50',
                    )}
                >
                    <TokenIcon token={market.coin.toLowerCase()} size="sm" />
                </span>

                <span className="pointer-events-none relative min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1">
                            {/* Slides itself into view on hover: `xyz:`
                                builder names are long enough to truncate, and
                                a half-read ticker is not an identification. */}
                            <MarqueeOnHover className="min-w-0 text-xs font-bold text-foreground">
                                {market.coin}
                            </MarqueeOnHover>
                            {held && (
                                // Visible, not just a tooltip: with several
                                // markets open this is how you find the ones you
                                // are in while scrolling ~230 rows.
                                <span
                                    aria-hidden
                                    title="Position open"
                                    className="size-1.5 shrink-0 rounded-full bg-primary shadow-[0_0_6px_rgba(200,242,36,0.6)]"
                                />
                            )}
                            {onToggleFavorite && (
                                <button
                                    type="button"
                                    aria-pressed={favorite}
                                    aria-label={
                                        favorite
                                            ? `Unpin ${market.coin}`
                                            : `Pin ${market.coin} to top`
                                    }
                                    onClick={onToggleFavorite}
                                    className={cn(
                                        // size-6 (24px) hit area for WCAG 2.5.8;
                                        // -my-1 keeps the row height unchanged.
                                        'pointer-events-auto -my-1 flex size-6 shrink-0 cursor-pointer items-center justify-center transition-opacity',
                                        favorite
                                            ? 'text-primary opacity-100'
                                            : 'text-muted-foreground opacity-35 hover:opacity-100 focus-visible:opacity-100',
                                    )}
                                >
                                    <Star
                                        size={10}
                                        className={
                                            favorite
                                                ? 'fill-current'
                                                : undefined
                                        }
                                    />
                                </button>
                            )}
                        </span>
                        {isSelected ? (
                            <LiveSelectedPrice
                                fallback={price}
                                markPx={market.markPx}
                            />
                        ) : (
                            price && (
                                <span className="font-mono text-xs font-semibold tabular-nums text-foreground">
                                    {price}
                                </span>
                            )
                        )}
                    </span>

                    <span className="mt-0.5 flex items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1.5">
                            {closes && closes.length >= 2 && (
                                <MarketSparkline closes={closes} dir={dir} />
                            )}
                            <span className="rounded border border-border/70 bg-background/40 px-1 font-mono text-[9px] leading-4 text-muted-foreground">
                                {market.maxLeverage}×
                            </span>
                            {/* On the card, not only in the `title`. The
                                tooltip carrying volume/funding/max-lev is
                                unreachable on a phone, and this sheet IS the
                                phone's market picker — so the one figure that
                                says "nothing trades here" has to be visible
                                without a hover. */}
                            {openInterest && (
                                <span className="truncate font-mono text-[9px] leading-4 text-muted-foreground">
                                    {openInterest}
                                </span>
                            )}
                            {unavailable && (
                                <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1 text-[9px] leading-4 text-amber-400">
                                    {unavailable}
                                </span>
                            )}
                            {/* A market this far from its oracle has, on the
                                live board without exception, no open interest
                                at all. Worth seeing BEFORE the market is
                                picked, not after. */}
                            {dislocation && (
                                <span className="rounded border border-destructive/40 bg-destructive/10 px-1 text-[9px] leading-4 text-destructive">
                                    {dislocation} off oracle
                                </span>
                            )}
                            {isolatedOnly && (
                                <span className="rounded border border-sky-400/30 bg-sky-400/10 px-1 text-[9px] leading-4 text-sky-300">
                                    isolated
                                </span>
                            )}
                        </span>
                        {change ? (
                            <span
                                className={cn(
                                    // st-market-change is the hook speed-ui.css
                                    // uses for the italic racing voice — a
                                    // dedicated class, because the old
                                    // utility-combo selector also caught the
                                    // LeverageSlider numerals.
                                    'st-market-change font-mono text-[11px] font-bold tabular-nums',
                                    change.dir > 0 && 'text-primary',
                                    change.dir < 0 && 'text-destructive',
                                    change.dir === 0 && 'text-muted-foreground',
                                )}
                            >
                                {change.text}
                            </span>
                        ) : (
                            // prevDayPx not served yet (older backend) —
                            // fall back to the old funding readout.
                            funding && (
                                <span
                                    className={cn(
                                        'font-mono text-[10px] tabular-nums',
                                        funding.startsWith('-')
                                            ? 'text-destructive'
                                            : 'text-muted-foreground',
                                    )}
                                >
                                    {funding}
                                </span>
                            )
                        )}
                    </span>
                </span>
            </div>
        );
    },
);

MarketRow.displayName = 'MarketRow';
